"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect, useState } from "react";
import { useLanguage } from "@/context/LanguageContext";

interface HeaderProps {
  activePage?: "home" | "solutions" | "research" | "blog" | "career";
}

export function Header({ activePage = "home" }: HeaderProps) {
  const { language, setLanguage } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (mobileMenuOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "unset";

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [mobileMenuOpen]);

  const toggleLanguage = () => {
    const nextLang = language === "en" ? "id" : "en";
    setLanguage(nextLang);
  };

  const labels = {
    en: {
      home: "Home",
      solutions: "Solutions",
      research: "Research",
      blog: "Blogs",
      career: "Careers",
      contact: "Contact Us",
    },
    id: {
      home: "Beranda",
      solutions: "Solusi",
      research: "Riset",
      blog: "Blog",
      career: "Karier",
      contact: "Hubungi Kami",
    },
  };

  const navLinks = [
    { href: "/", key: "home" },
    { href: "/solutions", key: "solutions" },
    { href: "/research", key: "research" },
    { href: "/blogs", key: "blog" },
    { href: "/careers", key: "career" },
  ] as const;

  return (
    <>
      {/* HEADER */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white font-sans shadow-sm">
        <div className="px-4 sm:px-8 lg:px-16 py-4">
          {/* Mobile row (no overlap) */}
          <div className="md:hidden flex items-center justify-between w-full">
            {/* Left: Hamburger + Logo */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="flex flex-col gap-1.5 w-8 h-8 justify-center items-center"
                aria-label="Open menu"
              >
                <span className="block w-6 h-0.5 bg-gray-800" />
                <span className="block w-6 h-0.5 bg-gray-800" />
                <span className="block w-6 h-0.5 bg-gray-800" />
              </button>

              <Link href="/" className="flex items-center">
                <Image
                  src="/homepage/homepage-picture/ganeshait.svg"
                  alt="GaneshaIT"
                  width={150}
                  height={27}
                  priority
                  className="w-28 h-auto"
                />
              </Link>
            </div>

            {/* Right: Language Toggle (mobile) */}
            <button
              onClick={toggleLanguage}
              className="relative flex items-center w-22 h-8 rounded-full border-2 border-[#0C1A2E] font-semibold text-xs overflow-hidden"
              aria-label="Toggle language"
            >
              <div
                className={`absolute top-0 left-0 h-full w-1/2 bg-[#0C1A2E] rounded-full transition-transform duration-300 ease-in-out ${language === "en" ? "translate-x-0" : "translate-x-full"
                  }`}
              />
              <span
                className={`relative z-10 flex-1 text-center transition-colors duration-300 ${language === "en" ? "text-white" : "text-[#0C1A2E]"
                  }`}
              >
                EN
              </span>
              <span
                className={`relative z-10 flex-1 text-center transition-colors duration-300 ${language === "id" ? "text-white" : "text-[#0C1A2E]"
                  }`}
              >
                ID
              </span>
            </button>
          </div>

          {/* Desktop grid (true centered nav) */}
          <div className="hidden md:grid grid-cols-[1fr_auto_1fr] items-center">
            {/* Left: Logo */}
            <div className="flex items-center">
              <Link href="/" className="flex items-center">
                <Image
                  src="/homepage/homepage-picture/ganeshait.svg"
                  alt="GaneshaIT"
                  width={150}
                  height={27}
                  priority
                  className="w-32 lg:w-36 h-auto"
                />
              </Link>
            </div>

            {/* Center: Nav (center of full header) */}
            <div className="flex justify-center">
              <div className="flex items-center gap-8">
                {navLinks.map((link) => {
                  const isActive = activePage === link.key;
                  return (
                    <Link
                      key={link.key}
                      href={link.href}
                      suppressHydrationWarning
                      className={`relative font-semibold transition-colors ${isActive
                        ? "text-[#0C2E6C]"
                        : "text-gray-600 hover:text-gray-900"
                        }`}
                    >
                      {labels[language][link.key]}
                      {isActive && (
                        <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-[#0C2E6C]" />
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center justify-end gap-3 lg:gap-4">
              {/* Language Toggle (desktop smaller) */}
              <button
                onClick={toggleLanguage}
                className="relative flex items-center w-23 h-9 rounded-full border-2 border-[#0C1A2E] font-semibold text-xs overflow-hidden"
                aria-label="Toggle language"
              >
                <div
                  className={`absolute top-0 left-0 h-full w-1/2 bg-[#0C1A2E] rounded-full transition-transform duration-300 ease-in-out ${language === "en" ? "translate-x-0" : "translate-x-full"
                    }`}
                />
                <span
                  className={`relative z-10 flex-1 text-center transition-colors duration-300 ${language === "en" ? "text-white" : "text-[#0C1A2E]"
                    }`}
                >
                  EN
                </span>
                <span
                  className={`relative z-10 flex-1 text-center transition-colors duration-300 ${language === "id" ? "text-white" : "text-[#0C1A2E]"
                    }`}
                >
                  ID
                </span>
              </button>

              {/* Contact button (desktop smaller) */}
              <Link
                href="/contact"
                suppressHydrationWarning
                className="rounded-[10px] p-0.5"
                style={{
                  background: "linear-gradient(135deg, #0C1A2E 0%, #285494 100%)",
                }}
              >
                <div
                  className="rounded-[10px] px-4 py-2 text-white font-semibold text-sm transition-opacity hover:opacity-90 w-35 text-center"
                  style={{
                    background:
                      "linear-gradient(135deg, #0C1A2E 0%, #285494 100%)",
                  }}
                >
                  {labels[language].contact}
                </div>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-60 md:hidden backdrop-blur-md bg-black/20"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full w-80 bg-white z-70 transform transition-transform duration-300 ease-in-out md:hidden ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
          }`}
      >
        <div className="flex flex-col h-full">
          {/* Menu Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-lg font-semibold text-gray-800">Menu</h2>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close menu"
            >
              <svg
                className="w-6 h-6 text-gray-800"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Menu Content */}
          <div className="flex-1 overflow-y-auto py-6">
            <nav className="flex flex-col space-y-1 px-4">
              {navLinks.map((link) => {
                const isActive = activePage === link.key;
                return (
                  <Link
                    key={link.key}
                    href={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    suppressHydrationWarning
                    className={`px-4 py-3 text-base font-medium rounded-lg transition-colors ${isActive
                      ? "text-[#0C2E6C] bg-blue-50"
                      : "text-gray-700 hover:bg-gray-50"
                      }`}
                  >
                    {labels[language][link.key]}
                  </Link>
                );
              })}

            </nav>
          </div>

          {/* Menu Footer - Contact Button */}
          <div className="p-6 border-t">
            <Link
              href="/contact"
              onClick={() => setMobileMenuOpen(false)}
              suppressHydrationWarning
              className="block w-full rounded-[10px] p-0.5"
              style={{
                background: "linear-gradient(135deg, #0C1A2E 0%, #285494 100%)",
              }}
            >
              <div
                className="rounded-[10px] px-6 py-3 text-white font-semibold text-center transition-opacity hover:opacity-90"
                style={{
                  background: "linear-gradient(135deg, #0C1A2E 0%, #285494 100%)",
                }}
              >
                {labels[language].contact}
              </div>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
